import { useEffect, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Plus, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls, statusBadge } from '../../utils/operations';

const DOC_TYPES = [
  ['terms', 'Terms & Conditions'],
  ['privacy', 'Privacy Policy'],
  ['cookies', 'Cookie Policy'],
  ['cancellation', 'Cancellation & Refund Policy'],
  ['affiliate_disclosure', 'Affiliate & Third-Party Disclosure'],
  ['partner_terms', 'Partner Terms'],
];
const LANGS = ['pt', 'en', 'de', 'fr'];
const TR_STATUSES = ['draft', 'translated', 'reviewed', 'published'];
const COMPANY_FIELDS = [
  ['trading_name', 'Trading Name'], ['legal_entity', 'Legal Entity'], ['registered_address', 'Registered Address'],
  ['country_of_registration', 'Country of Registration'], ['registration_number', 'Company Registration Number'],
  ['tax_number', 'Tax Identification Number'], ['website', 'Website'], ['general_enquiries', 'General Enquiries'],
  ['customer_support', 'Customer Support'], ['phone_whatsapp', 'WhatsApp / Phone'], ['partner_enquiries', 'Partner Enquiries'],
];

const CompanyInfo = ({ settings }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (settings) { setInfo(settings.company_info || {}); return; }
    apiFetch('/settings').then((s) => setInfo(s.company_info || {})).catch(() => setInfo({}));
  }, [settings]);
  if (!info) return null;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/settings', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ company_info: info }) });
      toast.success('Informação da empresa guardada');
    } catch (err) {
      toast.error('Erro ao guardar', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="company-info-form" onSubmit={save} className="rounded-xl border border-eco-ink/10 bg-white p-6">
      <h2 className="text-base font-semibold text-eco-ink">Company Information</h2>
      <p className="mt-1 text-xs text-eco-ink/50">Campos vazios aparecem publicamente como «a ser adicionado» — nunca inventes dados legais.</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {COMPANY_FIELDS.map(([key, label]) => (
          <label key={key}>
            <span className="eco-field-label mb-1 block">{label}</span>
            <input data-testid={`company-info-${key}`} value={info[key] || ''} onChange={(e) => setInfo((i) => ({ ...i, [key]: e.target.value }))} className={inputCls} />
          </label>
        ))}
      </div>
      <button data-testid="company-info-save" disabled={busy} className="eco-btn-primary mt-5">{busy && <Loader2 size={14} className="animate-spin" />} Guardar informação da empresa</button>
    </form>
  );
};

const DocEditor = ({ doc, onClose, onSaved }) => {
  const [lang, setLang] = useState('pt');
  const [translations, setTranslations] = useState(() => JSON.parse(JSON.stringify(doc.translations || {})));
  const [notes, setNotes] = useState(doc.notes || '');
  const [busy, setBusy] = useState(false);
  const tr = translations[lang] || { status: 'draft', sections: [] };

  const patchLang = (patch) => setTranslations((all) => ({ ...all, [lang]: { ...(all[lang] || { status: 'draft', sections: [] }), ...patch } }));
  const setSection = (i, k, v) => patchLang({ sections: tr.sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });

  const save = async () => {
    setBusy(true);
    try {
      await apiFetch(`/admin/legal/documents/${doc.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, translations }),
      });
      toast.success('Documento guardado');
      onSaved();
    } catch (err) {
      toast.error('Erro ao guardar', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    await save();
    try {
      await apiFetch(`/admin/legal/documents/${doc.id}/publish`, { method: 'POST' });
      toast.success(`Versão ${doc.version} publicada`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error('Não foi possível publicar', { description: String(err.message) });
    }
  };

  return (
    <div data-testid="legal-doc-editor" className="mt-5 rounded-xl border border-eco-ink/10 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-eco-ink">{DOC_TYPES.find(([id]) => id === doc.doc_type)?.[1]} · v{doc.version} <span className={`ml-2 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusBadge(doc.status)}`}>{doc.status}</span></h3>
        <button data-testid="legal-editor-close" onClick={onClose} className="text-eco-ink/40 hover:text-eco-ink"><X size={18} /></button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {LANGS.map((l) => {
          const st = (translations[l] || {}).status || 'draft';
          return (
            <button key={l} type="button" data-testid={`legal-lang-${l}`} onClick={() => setLang(l)} className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase transition-colors ${lang === l ? 'bg-eco-green text-eco-gold' : 'bg-eco-cream text-eco-ink/60 hover:text-eco-ink'}`}>
              {l} · <span className="normal-case font-medium">{st}</span>
            </button>
          );
        })}
        <label className="ml-auto flex items-center gap-2 text-xs font-semibold text-eco-ink/60">
          Estado da tradução ({lang.toUpperCase()}):
          <select data-testid="legal-translation-status" value={tr.status} onChange={(e) => patchLang({ status: e.target.value })} className={inputCls}>
            {TR_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-4 space-y-3">
        {tr.sections.map((s, i) => (
          <div key={i} data-testid={`legal-section-${i}`} className="rounded-xl bg-eco-cream p-4">
            <div className="flex items-center gap-2">
              <input data-testid={`legal-section-title-${i}`} value={s.title} onChange={(e) => setSection(i, 'title', e.target.value)} placeholder="Título da secção" className={`${inputCls} font-semibold`} />
              <button type="button" data-testid={`legal-section-remove-${i}`} onClick={() => patchLang({ sections: tr.sections.filter((_, j) => j !== i) })} className="grid min-h-10 w-10 shrink-0 place-items-center rounded-lg text-red-400 hover:bg-red-50"><X size={14} /></button>
            </div>
            <textarea rows={3} data-testid={`legal-section-body-${i}`} value={s.body} onChange={(e) => setSection(i, 'body', e.target.value)} placeholder="Conteúdo…" className={`${inputCls} mt-2`} />
          </div>
        ))}
        <button type="button" data-testid="legal-section-add" onClick={() => patchLang({ sections: [...tr.sections, { id: `sec-${Date.now()}`, title: '', body: '' }] })} className="eco-btn-secondary !min-h-10 px-4 text-xs"><Plus size={13} /> Adicionar secção</button>
      </div>

      <label className="mt-4 block">
        <span className="eco-field-label mb-1 block">Notas internas</span>
        <input data-testid="legal-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
      </label>

      <div className="mt-5 flex flex-wrap gap-2">
        <button data-testid="legal-save" onClick={save} disabled={busy} className="eco-btn-secondary !min-h-11">{busy && <Loader2 size={14} className="animate-spin" />} Guardar rascunho</button>
        {doc.status !== 'published' && (
          <button data-testid="legal-publish" onClick={publish} disabled={busy} className="eco-btn-primary"><Send size={14} /> Publicar versão {doc.version}</button>
        )}
        {doc.status === 'published' && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-eco-green"><CheckCircle2 size={15} /> Versão atual publicada</span>}
      </div>
      <p className="mt-3 text-[11px] text-eco-ink/45">Publicar arquiva automaticamente a versão anterior. Só traduções com estado «published» aparecem no site.</p>
    </div>
  );
};

export const AdminLegal = ({ settings }) => {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newVersion, setNewVersion] = useState(null);

  const load = async () => {
    try {
      setDocs(await apiFetch('/admin/legal/documents'));
    } catch (e) {
      toast.error('Erro ao carregar documentos', { description: String(e.message) });
    }
  };
  useEffect(() => { load(); }, []);

  const createVersion = async (e) => {
    e.preventDefault();
    try {
      const source = docs.filter((d) => d.doc_type === newVersion.doc_type).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
      await apiFetch('/admin/legal/documents', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_type: newVersion.doc_type, version: newVersion.version, notes: `Nova versão baseada em v${source?.version || '1.0'}`, translations: source?.translations || {} }),
      });
      toast.success('Nova versão criada como rascunho');
      setNewVersion(null);
      await load();
    } catch (err) {
      toast.error('Erro ao criar versão', { description: String(err.message) });
    }
  };

  return (
    <div data-testid="admin-legal" className="mt-8 space-y-8">
      <CompanyInfo settings={settings} />

      <div>
        <h2 className="text-base font-semibold text-eco-ink">Documentos legais & versões</h2>
        <p className="mt-1 text-xs text-eco-ink/50">Drafts nunca aparecem no site. Publica só depois de rever — traduções têm estados independentes (draft → translated → reviewed → published).</p>
        <div className="mt-4 space-y-3">
          {docs.map((d) => (
            <article key={d.id} data-testid={`legal-doc-${d.doc_type}-v${d.version}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-eco-ink/10 bg-white px-5 py-4">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-eco-cream text-eco-green"><FileText size={15} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-eco-ink">{DOC_TYPES.find(([id]) => id === d.doc_type)?.[1] || d.doc_type} · v{d.version}</p>
                <p className="mt-0.5 text-xs text-eco-ink/55">
                  {d.published_at ? `Publicado ${String(d.published_at).slice(0, 10)}` : 'Não publicado'}
                  {d.previous_version && ` · anterior v${d.previous_version}`}
                  {' · '}{LANGS.map((l) => `${l.toUpperCase()}:${(d.translations?.[l] || {}).status || 'draft'}`).join(' ')}
                </p>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(d.status)}`}>{d.status}</span>
              <button data-testid={`legal-edit-${d.id}`} onClick={() => setEditing(d)} className="eco-btn-secondary !min-h-9 px-3 text-xs">Editar</button>
            </article>
          ))}
          {docs.length === 0 && <p data-testid="legal-docs-empty" className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-8 text-center text-sm text-eco-ink/50">Sem documentos.</p>}
        </div>

        {newVersion ? (
          <form data-testid="legal-new-version-form" onSubmit={createVersion} className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-eco-ink/10 bg-white p-4">
            <label><span className="eco-field-label mb-1 block">Documento</span>
              <select data-testid="legal-new-type" value={newVersion.doc_type} onChange={(e) => setNewVersion((n) => ({ ...n, doc_type: e.target.value }))} className={inputCls}>
                {DOC_TYPES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </label>
            <label><span className="eco-field-label mb-1 block">Nova versão</span>
              <input required data-testid="legal-new-version" value={newVersion.version} onChange={(e) => setNewVersion((n) => ({ ...n, version: e.target.value }))} placeholder="1.1" className={inputCls} />
            </label>
            <button data-testid="legal-new-create" className="eco-btn-primary !min-h-10 text-xs"><Plus size={13} /> Criar rascunho</button>
            <button type="button" onClick={() => setNewVersion(null)} className="eco-btn-text text-xs">Cancelar</button>
          </form>
        ) : (
          <button data-testid="legal-new-version-btn" onClick={() => setNewVersion({ doc_type: 'terms', version: '' })} className="eco-btn-secondary mt-4 !min-h-10 px-4 text-xs"><Plus size={13} /> Nova versão de documento</button>
        )}
      </div>

      {editing && <DocEditor doc={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
};
